import dayjs from "dayjs"
import {
  EventStatus,
  EventType,
  FilmSessionFragment,
  FilmTileFragment,
  FilmTileWithSessionsFragment,
} from "../types.graphql"
import { useMemo } from "react"
import { useHydrating } from "eddev/routing"

type Args = {
  film?: FilmTileFragment | FilmTileWithSessionsFragment
  session?: FilmSessionFragment
  status?: EventStatus
  hydrating?: boolean
}

export type FilmBadgeData = {
  label: string
  type: "sold-out" | "selling-fast" | "past-event" | "free" | "on-sale" | "short" | "event"
}

const ENABLE_PAST_FILMS = true

/**
 * For a given film (and optional session), returns an array of badges to display on that film or session.
 */
export function getFilmBadges(args: Args) {
  const badges: FilmBadgeData[] = []

  let isPast = false

  if (ENABLE_PAST_FILMS && !args.hydrating) {
    if (args.session?.info?.dateTime) {
      isPast = dayjs
        .tz(undefined, "America/New_York")
        .isAfter(dayjs.tz(args.session.info?.dateTime!, "America/New_York"))
    } else if (args.film?.sessions && args.film?.sessions.length > 0) {
      const allSessionsPast = args.film.sessions.every((session) => {
        return dayjs.tz(undefined, "America/New_York").isAfter(dayjs.tz(session?.info?.dateTime!, "America/New_York"))
      })
      isPast = allSessionsPast
    }
  }

  if (args.film?.eventInfo?.badges?.length) {
    args.film?.eventInfo?.badges.forEach((badge) => {
      badges.push({ label: badge!, type: "event" })
    })
  } else if (args.film?.eventInfo?.eventType === EventType.Short) {
    badges.push({ label: "Short Film", type: "short" })
  } else if (args.film?.eventInfo?.eventType === EventType.Event) {
    badges.push({ label: "Event", type: "event" })
  } else if (args.film?.eventInfo?.eventType === EventType.MultiFilm) {
    badges.push({ label: "Shorts Collection", type: "event" })
  }

  if (isPast) {
    badges.push({ label: "Past Event", type: "past-event" })
  } else if (args.status) {
    const status = args.status
    if (status === EventStatus.Free) {
      badges.push({ label: "Free", type: "free" })
    } else if (status === EventStatus.SoldOut) {
      badges.push({ label: "At Rush", type: "sold-out" })
    } else if (status === EventStatus.SellingFast) {
      badges.push({ label: "Selling Fast", type: "selling-fast" })
    } else if (status === EventStatus.Normal) {
      // Nothing at the moment?
    }
  }

  return badges
}
